import { useSub } from "../../../../../common/subscription/useSub";
import {
  Box,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Spinner,
  Text,
} from "@chakra-ui/react";
import type { Content, Editor } from "@tiptap/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AiDraftModal } from "../../../../../common/ai/AiDraftModal";
import { CpvSelector } from "../../../../../common/cpv/CpvSelector";
import { useReadinessByField } from "../hooks/useReadinessByField";
import {
  TipTap,
  parseTiptapContent,
} from "../../../../../common/input/TipTap/TipTap";
import { getMarkdownFromTiptapContent } from "../../../../../common/input/TipTap/markdownUtils";
import { buildDescriptionMessages } from "../utils/descriptionAiPrompt";
import { ContractPeriodSection } from "../components/ContractPeriodSection";
import { AdvancedSettingsSection } from "../components/AdvancedSettingsSection";
import { StepNavigation } from "../../../../../common/StepNavigation";
import {
  type SourcingEventDto,
  type SourcingEventPatchDto,
  AiFeature,
  AiItemType,
  AiSuggestionItemType,
} from "../../../../../autogen/bff-api";
import {
  useEditor,
  useFieldErrorTranslation,
} from "../../../../../common/editing/save-coordinator";
import {
  inheritsScopeFromScheme,
  signsItsOwnContract,
} from "../utils/procedure";
import { useStepLeaveHeldBack } from "../components/stepLeaveBlockers";

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  isActive: boolean;
}

export const ClassificationStep = ({
  eventId,
  previousStep,
  nextStep,
  nextStepLabel,
  isActive,
}: Props) => {
  const sub = useSub();
  const { t, i18n } = useTranslation();
  const { state, updateField, fieldStatus, fieldError } = useEditor<
    SourcingEventDto,
    SourcingEventPatchDto
  >(eventId);
  const formatFieldError = useFieldErrorTranslation();
  const { readinessFor } = useReadinessByField(eventId);
  const descriptionEditorRef = useRef<Editor | null>(null);
  const [showDescriptionAi, setShowDescriptionAi] = useState(false);
  const [editorReady, setEditorReady] = useState(false);
  const isHeldBack = useStepLeaveHeldBack();

  // Every step of the wizard is mounted at once and hidden with display:none, so the editor is
  // ready long before this step is reached — and focus() does nothing while it is unrendered.
  const hasFocusedDescription = useRef(false);
  useEffect(() => {
    if (!isActive || hasFocusedDescription.current) return;
    const editor = descriptionEditorRef.current;
    if (!editor || !editor.isEmpty) return;
    hasFocusedDescription.current = true;
    editor.commands.focus("end", { scrollIntoView: false });
  }, [isActive, editorReady]);

  const publicContext =
    state.eventContext.type === "Public" ? state.eventContext : null;
  const title = state.eventData.title;
  const description = state.eventData.description ?? "";
  const cpvCodes = publicContext?.cpvCodes ?? [];
  const mainCodeCheck = readinessFor("MainCpvCodeMatchesContractNature");
  const mainCodeError = mainCodeCheck?.satisfied === false ? mainCodeCheck.error : undefined;
  const cpvFieldError = fieldError("publicContext.cpvCodes") ?? mainCodeError;
  const cpvHasAnyError = fieldStatus("publicContext.cpvCodes") === "error" || !!mainCodeError;
  const isSaving = (field: string) =>
    fieldStatus(field) === "saving" || fieldStatus(field) === "pending";

  return (
    <Box pb="8" pt="10">
      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8">
        <FormControl mb="2">
          <FormLabel>
            {t("Description")}{" "}
            {isSaving("eventData.description") && <Spinner size="xs" />}
          </FormLabel>
          <TipTap
            content={description}
            onUpdate={async (value) =>
              updateField({ eventData: { description: value } })
            }
            editable={true}
            debounceWait={300}
            onEditorReady={(editor) => {
              descriptionEditorRef.current = editor;
              setEditorReady(true);
            }}
            onAutofill={() => setShowDescriptionAi(true)}
            askFurtherAbout={{ source: t("Description") }}
          />
          <FormHelperText>
            {t("Describe the scope and requirements of this procurement")}
          </FormHelperText>
          {showDescriptionAi && (
            <AiDraftModal
              title={t("Suggest description with AI")}
              feature="se-public-description"
              insertsInto={{
                itemType: AiSuggestionItemType.Procurement,
                itemId: eventId,
              }}
              buildMessages={() =>
                buildDescriptionMessages({
                  title,
                  currentDescription: getMarkdownFromTiptapContent(description),
                  contractType: publicContext?.contractNature ?? undefined,
                  cpvCodes,
                  estimatedValueNok:
                    publicContext?.estimatedValue?.value ?? null,
                  language: i18n.language.startsWith("no") ? "no" : "en",
                })
              }
              onClose={() => setShowDescriptionAi(false)}
              onInsert={(json) => {
                descriptionEditorRef.current?.commands.setContent(
                  parseTiptapContent(json) as Content,
                  {
                    emitUpdate: true,
                  },
                );
                setShowDescriptionAi(false);
              }}
            />
          )}
        </FormControl>
      </Box>

      <Box
        borderWidth="1px"
        borderRadius="lg"
        p="5"
        mb="8"
        bg="smBackground"
        display={inheritsScopeFromScheme(publicContext) ? "none" : undefined}
      >
        <FormControl isInvalid={cpvHasAnyError}>
          <FormLabel>
            {t("CPV codes")}{" "}
            {isSaving("publicContext.cpvCodes") && <Spinner size="xs" />}
          </FormLabel>
          <CpvSelector
            value={cpvCodes}
            onChange={(next) =>
              updateField(
                { publicContext: { cpvCodes: next } },
                { debounceMs: 300 },
              )
            }
            isInvalid={cpvHasAnyError}
            mainCode={{ contractNature: publicContext?.contractNature }}
            suggestionContext={
              sub.allowsAiFeature(AiFeature.CpvSuggestions)
                ? {
                    title,
                    description,
                    contractType: publicContext?.contractNature ?? undefined,
                    item: { type: AiItemType.Procurement, id: eventId },
                  }
                : undefined
            }
          />
          {cpvFieldError && (
            <FormErrorMessage>
              {formatFieldError(cpvFieldError)}
            </FormErrorMessage>
          )}
        </FormControl>
      </Box>

      {signsItsOwnContract(publicContext?.procedureType) && (
        <ContractPeriodSection eventId={eventId} />
      )}
      <AdvancedSettingsSection eventId={eventId} />

      {isHeldBack && (
        <Text fontSize="sm" color="smError" mt="8" data-testid="step-held-back">
          {t("Correct the fields marked in red, then try again.")}
        </Text>
      )}
      <StepNavigation
        onPrevious={previousStep}
        onNext={nextStep}
        nextLabel={nextStepLabel}
      />
    </Box>
  );
};
